import axios from '../../api/axiosInstance';
import { STATUS, getProjectId, extractErrorInfo } from './sharedfunction';
import { astToText } from '../query/astToText';
import { textToIds, QueryValidationError } from '../query/textToIds';
import { labelsToFieldNames } from '../query/labelsToFieldNames';
import { getTestCaseList } from './testcase_api';

/**
 * Query API service for fetching query schema and executing advanced queries
 */

/**
 * Fetch query schema for a given entity
 * @param {string} entity - Entity type (default: 'testcase')
 * @param {string} projectId - Project ID (required for project-scoped enum values like labels, users)
 * @returns {Promise<{status: boolean, data?: object, error?: object}>}
 */
export const getQuerySchema = async (entity = 'testcase', projectId = null) => {
  try {
    const pid = projectId || getProjectId();
    if (!pid) {
      return {
        status: STATUS.ERROR,
        error: 'Project ID is required'
      };
    }
    
    const response = await axios.get(`/${pid}/query/query-schema`, {
      params: { entity }
    });

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error('Error fetching query schema:', error);
    return extractErrorInfo(error);
  }
};

/**
 * Parse text query to AST (parse-only)
 * @param {number|string} projectId - Project ID
 * @param {string} text - Query text (may contain usernames)
 * @param {Object} schema - Query schema (optional, used to convert usernames to IDs)
 * @returns {Promise<{status: boolean, data?: {ast}, error?: object}>}
 */
export const parseTextAPI = async (projectId, text, schema = null) => {
  try {
    const pid = projectId || getProjectId();
    if (!pid) {
      return {
        status: STATUS.ERROR,
        error: 'Project ID is required'
      };
    }

    let processedText = text;
    try {
      if (schema) {
        processedText = labelsToFieldNames(processedText, schema);
        processedText = textToIds(processedText, schema);
      }
    } catch (error) {
      if (error.name === 'QueryValidationError') {
        return {
          status: STATUS.ERROR,
          error: error.message,
          code: 'VALIDATION_ERROR',
          field: error.field,
          value: error.value
        };
      }
      throw error;
    }

    const { getUserTimezone } = require('../timezone');
    const userTimezone = getUserTimezone();

    const response = await axios.post(`/${pid}/query/parse`, {
      text: processedText ? String(processedText).trim() : '',
      timezone: userTimezone
    });

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error || 'Failed to parse query',
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error('Error parsing query text:', error);
    return extractErrorInfo(error);
  }
};

/**
 * Execute query with AST
 * @param {number|string} projectId - Project ID
 * @param {object} options - Query options
 * @param {object} options.ast - Canonical AST (required)
 * @param {number} [options.page] - Page number
 * @param {number} [options.pageSize] - Page size
 * @param {string} [options.sortField] - Sort field
 * @param {string} [options.sortOrder] - Sort order
 * @param {Object} [options.schema] - Query schema (optional, used for display formatting)
 * @param {string[]} [options.selectedTestCaseIds] - Optional array of test case IDs to filter
 * @returns {Promise<{status: boolean, data?: object, error?: object}>}
 */
export const executeQueryWithAST = async (projectId, options) => {
  try {
    const pid = projectId || getProjectId();
    if (!pid) {
      return {
        status: STATUS.ERROR,
        error: 'Project ID is required'
      };
    }

    const { ast, page = 1, pageSize = 25, sortField, sortOrder, schema, moduleId = 'all', selectedTestCaseIds } = options;
    
    const response = await getTestCaseList({
      page,
      pageSize,
      moduleId,
      sortField,
      sortOrder,
      ast,
      ...(selectedTestCaseIds && Array.isArray(selectedTestCaseIds) && selectedTestCaseIds.length > 0 && { selectedTestCaseIds })
    });
    
    if (response.status === STATUS.SUCCESS) {
      return {
        status: STATUS.SUCCESS,
        data: response.data || {},
        message: response.message,
      };
    }
    
    return {
      status: STATUS.ERROR,
      error: response.error || 'Query execution failed',
      errors: response.errors,
    };
  } catch (error) {
    console.error('Error executing query with AST:', error);
    return extractErrorInfo(error);
  }
};

/**
 * Execute an advanced text query
 * @param {number|string} projectId - Project ID (optional, will use context if not provided)
 * @param {object} options - Query options
 * @param {string} options.text - Query text (may contain usernames)
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.pageSize] - Page size (default: 25)
 * @param {string} [options.sortField] - Sort field
 * @param {string} [options.sortOrder] - Sort order ('ASC' | 'DESC')
 * @param {Object} schema - Query schema (optional, used to convert usernames to IDs)
 * @returns {Promise<{status: boolean, data?: object, error?: object}>}
 */
export const executeAdvancedQuery = async (projectId, options = {}, schema = null) => {
  try {
    const pid = projectId || getProjectId();
    if (!pid) {
      return {
        status: STATUS.ERROR,
        error: 'Project ID is required'
      };
    }

    const { text, page = 1, pageSize = 25, sortField, sortOrder, moduleId = 'all' } = options;

    let processedText = text;
    try {
      if (schema) {
        processedText = labelsToFieldNames(processedText, schema);
        processedText = textToIds(processedText, schema);
      }
    } catch (error) {
      if (error.name === 'QueryValidationError') {
        return {
          status: STATUS.ERROR,
          error: error.message,
          code: 'VALIDATION_ERROR',
          field: error.field,
          value: error.value
        };
      }
      throw error;
    }

    const { getUserTimezone } = require('../timezone');
    const userTimezone = getUserTimezone();

    const requestBody = {
      text: processedText ? String(processedText).trim() : '',
      page: Number(page),
      pageSize: Number(pageSize),
      timezone: userTimezone,
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder }),
      ...(moduleId && moduleId !== 'all' && { moduleId })
    };
    
    const response = await axios.post(`/${pid}/query/advanced`, requestBody);

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error('Error executing advanced query:', error);
    return extractErrorInfo(error);
  }
};

